import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { preflight, readableError, sourceFor } from './commands';
const b1 = require('./b1');
const { scopeKey } = require('./b1/contracts');

// The live connector and isolated test connectors share the same UI contract.
export default function useCompositionEditor(connector) {
  const [model, setModel] = useState(null), [scopeIndex, setScopeIndex] = useState(0);
  const [selected, setSelected] = useState({}), [folds, setFolds] = useState({});
  const [status, setStatus] = useState('loading'), [error, setError] = useState(null);
  const [drafts, setDrafts] = useState({}), [detailTab, setDetailTab] = useState('Général');
  const [scopeDrafts, setScopeDrafts] = useState({});
  const accept = useCallback(value => { setModel(value); setStatus(value.state); }, []);
  const busy = useRef(false), mounted = useRef(false), lastMapping = useRef(null);
  useEffect(() => {
    mounted.current = true;
    setDrafts({}); setScopeDrafts({}); setSelected({}); setFolds({}); setScopeIndex(0); setError(null); setStatus('loading');
    if (!connector) { setModel(null); setStatus('idle'); return () => { mounted.current = false; }; }
    if (connector.initialModel) { setModel(connector.initialModel); setStatus(connector.initialModel.state); }
    else connector.load().then(value => { if (mounted.current) { setModel(value); setStatus(value.state); } })
      .catch(failure => { if (mounted.current) { setError(failure); setStatus('engine-error'); } });
    return () => { mounted.current = false; };
  }, [connector]);
  const entry = model?.scopes[scopeIndex] || model?.scopes[0];
  const validation = useMemo(() => entry ? b1.validateVatProtections(sourceFor(model, entry)) : null, [model, entry]);
  const graph = validation?.graph;
  const id = selected[scopeIndex] ?? graph?.nodes[0]?.id;
  const line = entry?.lines.find(l => l.id === id);
  const select = next => {
    const mapping = lastMapping.current;
    const mapped = entry?.scope.kind === 'option' ? mapping?.options?.[entry.scope.optionId]?.lines?.[next] : mapping?.lines?.[next];
    setSelected(old => ({ ...old, [scopeIndex]: mapped || next }));
  };
  const run = async command => {
    if (busy.current || !model?.permissions.canEdit || model.status !== 'draft' || model.contentSchemaVersion !== 2) return false;
    const checked = preflight(model, entry, command);
    if (!checked.valid) { setError(checked.errors[0]); return false; }
    busy.current = true; setError(null); setStatus('calculating');
    try {
      const value = await connector.execute({ quoteVersionId: model.quoteVersionId, expectedRevision: model.revision, command });
      if (!mounted.current) return false;
      const mapping = value.idMapping;
      lastMapping.current = mapping;
      if (mapping) setSelected(old => Object.fromEntries(Object.entries(old).map(([index, selectedId]) => {
        const previousScope = model.scopes[Number(index)]?.scope;
        const mapped = previousScope?.kind === 'option' ? mapping.options?.[previousScope.optionId]?.lines?.[selectedId] : mapping.lines?.[selectedId];
        return [index, mapped || selectedId];
      })));
      setModel(value); setStatus(value.state); return true;
    } catch (failure) {
      if (mounted.current) {
        const code = failure.code || failure.body?.code || failure.body?.error?.code;
        setError({ code, message: failure.message, status: failure.status });
        setStatus(code === 'QUOTE_REVISION_CONFLICT' ? 'conflict' : failure.status >= 500 ? 'engine-error' : model.state);
      }
      return false;
    } finally { busy.current = false; }
  };
  const reload = async () => {
    if (busy.current || !connector) return;
    if (connector.beforeReload && !connector.beforeReload()) return;
    busy.current = true; setStatus('loading'); setError(null);
    try { const value = await connector.load(); setDrafts({}); setScopeDrafts({}); setModel(value); setStatus(value.state); }
    catch (failure) { setError(failure); setStatus('engine-error'); }
    finally { busy.current = false; }
  };
  const collapsed = folds[scopeIndex] || new Set();
  const toggle = nodeId => setFolds(old => { const next = new Set(old[scopeIndex] || []); if (next.has(nodeId)) next.delete(nodeId); else next.add(nodeId); return { ...old, [scopeIndex]: next }; });
  const pendingMutations = (model?.scopes || []).flatMap(item => [
    ...item.lines.filter(node => Object.keys(drafts[`${scopeKey(item.scope)}:${node.id}`] || {}).length).map(node => ({ type: 'patchLine', scope: item.scope, lineId: node.id, patch: drafts[`${scopeKey(item.scope)}:${node.id}`] })),
    ...(Object.keys(scopeDrafts[JSON.stringify(item.scope)] || {}).length ? [{ type: 'patchScope', scope: item.scope, patch: scopeDrafts[JSON.stringify(item.scope)] }] : []),
  ]);
  return { model, entry, graph, tax: validation?.tax, scopeIndex, setScopeIndex, line, select, collapsed, toggle, drafts, setDrafts, scopeDrafts, setScopeDrafts, detailTab, setDetailTab,
    accept, pendingMutations, hasPendingChanges: pendingMutations.length > 0, clearPending: () => { setDrafts({}); setScopeDrafts({}); },
    status: validation && !validation.valid ? 'invalid' : status,
    error: error ? readableError(error) : validation && !validation.valid ? readableError(validation.errors[0]) : null,
    run, reload, canEdit: !!model?.permissions.canEdit && model.status === 'draft' && model.contentSchemaVersion === 2
      && !!validation?.valid && !['loading', 'calculating', 'conflict'].includes(status),
    reliable: !!validation?.valid && status === 'complete' && model?.state === 'complete' };
}
